<template>
  <div class="member-form tongxin-layout">
    <h1 class="title-with-underline">
      請輸入掛號對象資料
    </h1>

    <form @submit.prevent="handleSubmit">
      <!-- 姓名 -->
      <div class="form-group">
        <label class="required">姓名</label>
        <input
          v-model="formData.name"
          @input="handleNameInput"
          type="text"
          class="form-control"
          placeholder="請輸入姓名"
          maxlength="50"
          required
        />
        <div class="error-message" v-if="errors.name">{{ errors.name }}</div>
      </div>

      <!-- 身分證字號 -->
      <div class="form-group">
        <label class="required">身分證字號</label>
        <input
          v-model="formData.idno"
          type="text"
          class="form-control"
          placeholder="請輸入身分證字號"
          maxlength="10"
          required
        />
        <div class="error-message" v-if="errors.idno">{{ errors.idno }}</div>
      </div>

      <!-- 出生年月日 -->
      <div class="form-group">
        <label class="required">出生年月日</label>
        <input
          v-model="formData.birthday"
          type="date"
          class="form-control"
          required
        />
        <div class="error-message" v-if="errors.birthday">{{ errors.birthday }}</div>
      </div>

      <!-- 手機號碼 -->
      <div class="form-group">
        <label class="required">手機號碼</label>
        <input
          v-model="formData.mobile"
          type="tel"
          class="form-control"
          placeholder="請輸入手機號碼（09xxxxxxxx）"
          maxlength="10"
          inputmode="numeric"
          required
        />
        <div class="error-message" v-if="errors.mobile">{{ errors.mobile }}</div>
      </div>
    </form>

    <div style="padding-bottom: 96px"></div>
    <div class="fixed-footer-navigation">
      <TongxinNavigation
        :canProceed="!isLoading"
        :backButtonIsVisible="true"
        :nextButtonIsVisible="true"
        :textForNextStep="'下一步'"
        @back="handleBack"
        @next="handleNext"
      />
    </div>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <b-spinner style="border-width: 2px" />
    </div>
  </div>
</template>

<script>
import familyManager from '@/mixins/web/tongxin/familyManager'
import TongxinNavigation from '@/components/web/proj/tongxin/layout/TongxinNavigation.vue'

export default {
  name: 'TongxinFamilyMemberForm',
  mixins: [familyManager],
  components: {
    TongxinNavigation,
  },
  props: {
    memberId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        name: '',
        idno: '',
        birthday: '',
        mobile: '',
      },
      errors: {},
      currentMember: null,
    }
  },
  computed: {
    isBookingFlow() {
      return this.$route.query.bookingFlow === '1'
    },
    bookingReturnStep() {
      return this.$route.query.returnStep || 'confirmation'
    },
    bookingBackStep() {
      return this.$route.query.backStep || 'time-selection'
    },
    isLoading() {
      return this.isCallingFamilyApi
    },
  },
  created() {
  },
  methods: {
    validateName(name) {
      const trimmedName = (name || '').trim()
      if (!trimmedName) {
        return ''
      }

      const namePattern = /^(?=.{1,50}$)[\p{L}]+(?:[ 　][\p{L}]+)*$/u
      if (!namePattern.test(trimmedName)) {
        return '不可包含特殊符號、數字，且長度不可超過 50 個字元'
      }

      return ''
    },

    handleNameInput() {
      const nameError = this.validateName(this.formData.name)
      if (nameError) {
        this.$set(this.errors, 'name', nameError)
        return
      }
      if (this.errors.name) {
        this.$delete(this.errors, 'name')
      }
    },

    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.formData.name || !this.formData.name.trim()) {
        this.errors.name = '請輸入姓名'
        isValid = false
      } else {
        const nameError = this.validateName(this.formData.name)
        if (nameError) {
          this.errors.name = nameError
          isValid = false
        }
      }

      if (!this.formData.idno || !this.formData.idno.trim()) {
        this.errors.idno = '請輸入身分證字號'
        isValid = false
      } else if (!/^[A-Z][A-Z0-9]\d{8}$/.test(this.formData.idno.trim())) {
        this.errors.idno = '身分證字號或居留證號格式不正確'
        isValid = false
      }

      if (!this.formData.birthday) {
        this.errors.birthday = '請選擇出生年月日'
        isValid = false
      }

      if (!this.formData.mobile || !this.formData.mobile.trim()) {
        this.errors.mobile = '請輸入手機號碼'
        isValid = false
      } else if (!/^09\d{8}$/.test(this.formData.mobile.trim())) {
        this.errors.mobile = '手機號碼格式不正確'
        isValid = false
      }

      return isValid
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }

      if (this.isBookingFlow) {
        const registrant = this.buildRegistrant()
        this.saveRegistrantToTongxinContext(registrant)
        this.goBackToBooking(this.bookingReturnStep)
        return
      }

      this.$router.push({ name: 'TongxinFamilyList' })
    },

    /**
     * 將 SAW API 生日格式轉換為 date input 格式
     * "1990/5/15 上午 12:00:00" → "1990-05-15"
     */
    formatBirthday(birthday) {
      if (!birthday) return ''
      // 已經是 YYYY-MM-DD 格式
      if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) return birthday
      // SAW API 格式：取日期部分，轉換斜線為橫線
      const datePart = birthday.split(' ')[0]
      if (!datePart) return ''
      const parts = datePart.split('/')
      if (parts.length !== 3) return ''
      const year = parts[0]
      const month = parts[1].padStart(2, '0')
      const day = parts[2].padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    goBack() {
      this.$router.back()
    },

    handleBack() {
      if (this.isBookingFlow) {
        this.goBackToBooking(this.bookingBackStep)
        return
      }
      this.goBack()
    },

    handleNext() {
      this.handleSubmit()
    },

    goBackToBooking(stepId) {
      this.$router.push({
        name: 'TongxinStep',
        params: {
          org_code: this.$route.params.org_code || this.$route.query.org_code,
          stepId,
          categoryId: this.$route.query.categoryId || undefined,
        },
        query: { ...this.$route.query },
      })
    },

    buildRegistrant() {
      return {
        rowid: 'self',
        source: 'self',
        name: this.formData.name?.trim() || '',
        ssid: this.formData.idno?.trim() || '',
        birthday: this.formData.birthday || '',
        mobile: this.formData.mobile?.trim() || '',
        relationship: '本人',
      }
    },

    saveRegistrantToTongxinContext(registrant) {
      try {
        const raw = localStorage.getItem('tongxin-context')
        const parsed = raw ? JSON.parse(raw) : {}
        const context = parsed.context || {}
        context.registrant = registrant
        parsed.context = context
        localStorage.setItem('tongxin-context', JSON.stringify(parsed))
      } catch (error) {
        console.error('儲存掛號對象失敗:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.member-form {
  form {
    margin-top: 40px;

    .form-group {
      margin-bottom: 24px;

      label {
        display: block;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;

        &.required::after {
          content: ' *';
          color: #dc3545;
        }
      }

      .form-control {
        width: 100%;
        padding: 12px;
        font-size: 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        transition: border-color 0.3s;

        &:focus {
          outline: none;
          border-color: #207868;
        }

        &::placeholder {
          color: #999;
        }
      }

      select.form-control {
        cursor: pointer;
      }

      .error-message {
        color: #dc3545;
        font-size: 14px;
        margin-top: 4px;
      }
    }

    .reminder-green {
      background: #e8f5f3;
      border-left: 4px solid #207868;
      padding: 16px;
      margin-bottom: 24px;
      border-radius: 4px;
      font-size: 14px;
      color: #333;
      line-height: 1.6;
    }

    .btn {
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 8px;
      transition: all 0.3s;

      &.btn-primary {
        background: #207868;
        border: none;
        color: white;

        &:hover:not(:disabled) {
          background: #186052;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      &.btn-secondary {
        background: #6c757d;
        border: none;
        color: white;

        &:hover {
          background: #5a6268;
        }
      }

      &.btn-outline-danger {
        background: white;
        border: 2px solid #dc3545;
        color: #dc3545;

        &:hover:not(:disabled) {
          background: #dc3545;
          color: white;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
